// SettingsNamePane.jsx

import React, { useCallback, useEffect, useState } from 'react';

// Context
import { useBase } from 'context/BaseContext';

// Services
import { updateBase } from 'services/bases';

// CSS
import './SettingsNamePane.css';

// Components
import SettingsModalPaneHeader from './SettingsModalPaneHeader';
import SettingsModalPaneContents from './SettingsModalPaneContents';
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

/**
 * SettingsNamePane
 *
 * Renders an editable field for the space display name.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} base - Active base model.
 * @param {boolean} canEditBase - Whether the current user can edit space settings.
 *
 * @returns {ReactNode} The rendered name settings pane.
 */
const SettingsNamePane = React.memo(({
    theme,
    base,
    canEditBase,
}) => {

    // ----- CONTEXT ----- //

    // Base Context

    const { setActiveBase } = useBase();

    // ----- STATE ----- //

    // Controlled input value.

    const [nameValue, setNameValue] = useState(base?.title || '');

    // Save in-flight state.
    const [isSaving, setIsSaving] = useState(false);

    // Error message state.
    const [errorMessage, setErrorMessage] = useState('');

    // Status message state.
    const [statusMessage, setStatusMessage] = useState('');

    // Whether the space key was recently copied.
    const [keyCopied, setKeyCopied] = useState(false);

    // ----- EFFECTS ----- //

    // Keep the input in sync when the space prop changes.

    useEffect(() => {
        setNameValue(base?.title || '');
    }, [base?.title]);

    // Whether the value differs from the persisted title.
    const hasChanges = nameValue.trim() !== (base?.title || '').trim();

    // ----- HANDLERS ----- //

    /**
     * Updates controlled input value and clears stale messages.
     *
     * @param {object} event - Native input change event.
     */

    const handleChange = (event) => {
        setNameValue(event.target.value);
        setErrorMessage('');
        setStatusMessage('');
    };

    /**
     * Persists the new space name via the write service.
     */
    const handleSave = async () => {
        if (isSaving || !base?.key) return;

        const trimmed = nameValue.trim();
        if (!trimmed) {
            setErrorMessage('Space name cannot be empty.');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Saving...');
        setIsSaving(true);

        try {
            await updateBase(base.key, { title: trimmed });
            setActiveBase((previous) => {
                if (!previous || previous.key !== base.key) return previous;
                return { ...previous, title: trimmed };
            });
            setStatusMessage('Space name updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update space name.');
            setStatusMessage('');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Copies the space key to the clipboard for support requests.
     */
    const handleCopyBaseKey = useCallback(async () => {
        const baseKey = base?.key;
        if (!baseKey) return;

        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            setErrorMessage('Copy is not available in this browser.');
            return;
        }

        try {
            await navigator.clipboard.writeText(baseKey);
            setKeyCopied(true);
            setTimeout(() => {
                setKeyCopied(false);
            }, 1500);
        } catch (error) {
            console.error('Failed to copy space key:', error);
            setErrorMessage('Failed to copy space key.');
        }
    }, [base?.key]);

    // ----- RENDER ----- //

    return (
        <>
            <SettingsModalPaneHeader
              title="About"
              subtitle="Base name and unique identifier."
            />

            <SettingsModalPaneContents>
                {/* ABOUT FIELDS */}
                <div className="settings-modal-about-fields">
                    {/* ABOUT FIELD */}
                    <div className="settings-modal-about-field">
                        {/* FIELD LABEL */}
                        <div
                          className="settings-modal-field-label"
                        >
                            Base name
                        </div>

                        <div
                          className="settings-modal-name-card"
                          style={{
                            borderColor: theme.backgroundHover,
                          }}
                        >
                            <TextInput
                              value={nameValue}
                              disabled={!canEditBase || isSaving}
                              maxLength={100}
                              width="100%"
                              onChange={handleChange}
                              onEnterPress={hasChanges ? handleSave : undefined}
                            />

                            <Button
                              iconKey="save"
                              label="Save"
                              enabled={canEditBase && hasChanges && !isSaving}
                              onClick={handleSave}
                            />
                        </div>
                    </div>

                    {/* ABOUT FIELD */}
                    <div className="settings-modal-about-field">
                        {/* FIELD LABEL */}
                        <div
                          className="settings-modal-field-label"
                        >
                            Base key
                        </div>

                        {/* KEY ROW */}
                        <div className="settings-modal-key-row">
                            {/* KEY VALUE */}
                            <div
                              className="settings-modal-key-value"
                            >
                                {base?.key || '—'}
                            </div>

                            <Button
                              backgroundColor="transparent"
                              enabled={Boolean(base?.key)}
                              height={38}
                              iconKey="content_copy"
                              iconSize={22}
                              iconColor={theme.foreground}
                              label=""
                              title={keyCopied ? 'Copied' : 'Copy space key'}
                              width={38}
                              onClick={handleCopyBaseKey}
                            />

                        </div>
                    </div>
                </div>

                {!canEditBase && (
                    <div
                      className="settings-modal-note"
                      style={{
                        color: theme.foregroundMuted,
                      }}
                    >
                        Only admins and owners can update the space name.
                    </div>
                )}

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}
            </SettingsModalPaneContents>
        </>
    );
});

// Dev Tools
SettingsNamePane.displayName = 'SettingsNamePane';

export default SettingsNamePane;
